import { useFloating } from "@floating-ui/react";
import React, {
  FC,
  HTMLAttributes,
  SVGProps,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import { useOutsideClick } from "../../shared";
import { tombac } from "../../shared/tombac";
import { Button } from "../Button";

export interface NavbarCollapseProps extends HTMLAttributes<HTMLElement> {
  breakpoint?: number;
}

export const NavbarCollapse: FC<NavbarCollapseProps> = ({
  breakpoint = 768,
  children,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { x, y, refs, strategy, update } = useFloating<HTMLDivElement>({
    middleware: [{ name: "zeroXOffset", fn: () => ({ x: 0 }) }],
    placement: "bottom-start",
  });

  useEffect(() => {
    if (isOpen) {
      update();
    }
  }, [isOpen, update]);

  useOutsideClick(refs.domReference.current, () => setIsOpen(false));

  return (
    <Container ref={refs.setReference} {...rest}>
      <Children breakpoint={breakpoint}>{children}</Children>
      <ExpandButton
        aria-label={isOpen ? "Collapse menu" : "Show menu"}
        breakpoint={breakpoint}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
        shape="circle"
        variant="flat"
      >
        <HamburgerIcon aria-hidden />
      </ExpandButton>
      <Menu
        breakpoint={breakpoint}
        isOpen={isOpen}
        onClick={() => setIsOpen(false)}
        ref={refs.setFloating}
        style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
      >
        <MenuInner breakpoint={breakpoint} isOpen={isOpen}>
          {children}
        </MenuInner>
      </Menu>
    </Container>
  );
};

const Children = styled.div<Required<Pick<NavbarCollapseProps, "breakpoint">>>`
  align-items: center;
  align-self: stretch;
  background: ${tombac.color.white};
  display: flex;
  flex: auto;

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    box-shadow: 0 ${tombac.unit(2, 8)} 0 hsla(0, 0%, 0%, 0.04);
    left: 0;
    overflow: hidden;
    position: fixed;
    right: 0;
    top: 100%;
    transition: max-height 0.1s ease-out;
  }
`;

const Container = styled.div`
  align-self: stretch;
  display: flex;
  flex: auto;
`;

const HamburgerIcon = styled((props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="4 -4 16 24" height="24" width="24" {...props}>
    <path d="M4 14h16v-2H4zm0-5h16V7H4zm0-5h16V2H4z" fill="currentColor" />
  </svg>
))`
  display: block;
`;

const ExpandButton = styled(Button)<
  Required<Pick<NavbarCollapseProps, "breakpoint">>
>`
  align-self: center;
  display: none;
  margin-left: auto;
  margin-right: ${tombac.space(2)};

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    display: flex;
  }
`;

const Menu = styled.div<{ breakpoint: number; isOpen: boolean }>`
  display: none;
  overflow: hidden;
  padding-bottom: ${tombac.unit(8)};
  position: relative;
  width: 100%;

  &::after {
    box-shadow: inset 0 ${tombac.unit(10, 8, -8)} hsla(0, 0%, 0%, 0.04);
    content: "";
    height: ${tombac.unit(8)};
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    position: absolute;
    top: 0;
    transition: opacity 0.1s ease-out;
    visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
    width: 100%;
  }

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    display: block;
  }
`;

const MenuInner = styled.div<
  Required<Pick<NavbarCollapseProps, "breakpoint">> & { isOpen: boolean }
>`
  background-color: ${tombac.color.white};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: translate(0, ${({ isOpen }) => (isOpen ? "0" : "-100%")});
  transition: 0.1s ease-out;
  transition-property: opacity, transform;
  visibility: visible;

  &::after {
    bottom: ${tombac.unit(-8)};
    box-shadow: inset 0 ${tombac.unit(10, 8, -8)} hsla(0, 0%, 0%, 0.04);
    content: "";
    height: ${tombac.unit(8)};
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    position: absolute;
    transition: opacity 0.1s ease-out;
    visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
    width: 100%;
  }
`;
